import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import {
  BusAdmin,
  BusAdminStateTypes,
  Login,
  RolePermission,
} from "./BusAdmin.util";

const name = "bus-admins";
const extraActions = createExtraActions();

const BusAdminInitialState: BusAdminStateTypes = {
  login: ResetApiState(null),
  resetLogin: ResetApiState(null),
  checkUser: ResetApiState(null),
  resetCheckUser: ResetApiState(null),
  createBusAdmin: ResetApiState(null),
  resetCreateBusAdmin: ResetApiState(null),
  editBusAdmin: ResetApiState(null),
  resetEditBusAdmin: ResetApiState(null),
  deleteBusAdmin: ResetApiState(null),
  resetDeleteBusAdmin: ResetApiState(null),
  busAdmins: ResetApiState([]),
  pagedBusAdmins: ResetApiState(ResetPaginationState()),
  busAdmin: ResetApiState(null),
  resetBusAdmin: ResetApiState(null),
  resetPassword: ResetApiState(null),
  resetResetPassword: ResetApiState(null),
  resetStates: ResetApiState(null),
  rolePermissions: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const getRolePermissions = createAsyncThunk<
    RolePermission[],
    { token: string }
  >(`/get-role-permissions-${name}`, async ({ token }, thunkApi) =>
    axios
      .get("/access-control/role-permission", {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-api-key": "zR0ClHzLGi",
        },
      })
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      })
  );

  const login = createAsyncThunk<ResponseWrapper<Login>, any>(
    `/login-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}/auth/login`, data, {
          headers: {
            "x-api-key": "zR0ClHzLGi",
          },
        })
        .then((response) => {
          if (
            response?.data?.success !== undefined &&
            response?.data?.success
          ) {
            console.log({ response: response.data });
            return response.data;
          } else {
            if (response?.status !== undefined)
              return thunkApi.rejectWithValue(response?.data.message);
            else
              thunkApi.rejectWithValue(
                "Oops, seems server is down, try again in a moment!"
              );
          }
        })
        .catch((error: AxiosError) => {
          console.log("_____________________", error);
          return thunkApi.rejectWithValue(error);
        })
  );

  const resetLogin = createAction(`/reset-login-${name}`);

  const checkUser = createAsyncThunk<ResponseWrapper<Login>, any>(
    `/checkuser-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}/auth/check-user`, data, {
          headers: {
            "x-api-key": "zR0ClHzLGi",
          },
        })
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          console.log("_____________________", error);
          return thunkApi.rejectWithValue(error);
        })
  );

  const resetCheckUser = createAction(`/reset-check-user-${name}`);

  const createBusAdmin = createAsyncThunk<ResponseWrapper<BusAdmin>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateBusAdmin = createAction(`/reset-create-${name}`);

  const editBusAdmin = createAsyncThunk<ResponseWrapper<BusAdmin>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data, {})
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditBusAdmin = createAction(`/reset-edit-${name}`);

  const deleteBusAdmin = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteBusAdmin = createAction(`/reset-delete-${name}`);

  const getBusAdmins = createAsyncThunk<BusAdmin[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedBusAdmins = createAsyncThunk<
    ResponseWrapper<PaginationType<BusAdmin[]>>,
    any
  >(`/get-paged-${name}s`, async (query: any, thunkApi) => {
    return axios
      .get(`/${name}/paged${queryStringGenerator(query)}`)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getBusAdmin = createAsyncThunk<ResponseWrapper<BusAdmin>, any>(
    `/get-${name}`,
    async (token: any, thunkApi) =>
      axios
        .get(`${name}/profile`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetBusAdmin = createAction(`/reset-get-${name}`);

  const resetPassword = createAsyncThunk<ResponseWrapper<any>, any>(
    `/reset-${name}-password`,
    async (id: any, thunkApi) =>
      axios
        .post(`/${name}/reset/${id}`)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetResetPassword = createAction(`/reset-reset-${name}-password`);

  const resetStates = createAction(`/reset-states`);

  return {
    login,
    resetLogin,
    checkUser,
    resetCheckUser,
    createBusAdmin,
    resetCreateBusAdmin,
    editBusAdmin,
    resetEditBusAdmin,
    deleteBusAdmin,
    resetDeleteBusAdmin,
    getBusAdmins,
    getPagedBusAdmins,
    getBusAdmin,
    resetBusAdmin,
    resetPassword,
    resetResetPassword,
    resetStates,
    getRolePermissions,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<BusAdminStateTypes>
) {
  return {
    ...loginReducer(),
    ...resetLoginReducer(),
    ...checkUserReducer(),
    ...resetCheckUserReducer(),
    ...createBusAdminReducer(),
    ...resetCreateBusAdmin(),
    ...editBusAdminReducer(),
    ...resetEditBusAdmin(),
    ...deleteBusAdminReducer(),
    ...resetDeleteBusAdmin(),
    ...busAdminsReducer(),
    ...pagedBusAdminsReducer(),
    ...busAdminReducer(),
    ...resetBusAdmin(),
    ...resetPasswordReducer(),
    ...resetResetPassword(),
    ...resetStatesReducer(),
    ...rolePermissionsReducer(),
  };

  function loginReducer() {
    return {
      ...builder.addCase(extraActions.login.pending, (state, action) => {
        state.login = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.login.fulfilled, (state, action) => {
        state.login = {
          loading: false,
          payload: action.payload?.data,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.login.rejected, (state, action) => {
        state.login = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function rolePermissionsReducer() {
    return {
      ...builder.addCase(
        extraActions.getRolePermissions.pending,
        (state, action) => {
          state.rolePermissions = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getRolePermissions.fulfilled,
        (state, action) => {
          state.rolePermissions = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };

          // Update login state with permissions
          if (state.login.payload) {
            state.login.payload.permissions = action.payload;
          }
        }
      ),
      ...builder.addCase(
        extraActions.getRolePermissions.rejected,
        (state, action) => {
          state.rolePermissions = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetLoginReducer() {
    return {
      ...builder.addCase(extraActions.resetLogin, (state, action) => {
        state.resetLogin = ResetApiState(true);
        state.login = ResetApiState(null);
      }),
    };
  }

  function checkUserReducer() {
    return {
      ...builder.addCase(extraActions.checkUser.pending, (state, action) => {
        state.checkUser = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.checkUser.fulfilled, (state, action) => {
        state.checkUser = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.checkUser.rejected, (state, action) => {
        state.checkUser = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetCheckUserReducer() {
    return {
      ...builder.addCase(extraActions.resetCheckUser, (state, action) => {
        state.checkUser = ResetApiState(null);
      }),
    };
  }

  function createBusAdminReducer() {
    return {
      ...builder.addCase(
        extraActions.createBusAdmin.pending,
        (state, action) => {
          state.createBusAdmin = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createBusAdmin.fulfilled,
        (state, action) => {
          state.createBusAdmin = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createBusAdmin.rejected,
        (state, action) => {
          state.createBusAdmin = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateBusAdmin() {
    return {
      ...builder.addCase(extraActions.resetCreateBusAdmin, (state, action) => {
        state.resetCreateBusAdmin = ResetApiState(true);
        state.createBusAdmin = ResetApiState(null);
      }),
    };
  }

  function editBusAdminReducer() {
    return {
      ...builder.addCase(extraActions.editBusAdmin.pending, (state, action) => {
        state.editBusAdmin = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.editBusAdmin.fulfilled,
        (state, action) => {
          state.editBusAdmin = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editBusAdmin.rejected,
        (state, action) => {
          state.editBusAdmin = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditBusAdmin() {
    return {
      ...builder.addCase(extraActions.resetEditBusAdmin, (state, action) => {
        state.resetEditBusAdmin = ResetApiState(true);
        state.editBusAdmin = ResetApiState(null);
      }),
    };
  }

  function deleteBusAdminReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteBusAdmin.pending,
        (state, action) => {
          state.deleteBusAdmin = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteBusAdmin.fulfilled,
        (state, action) => {
          state.deleteBusAdmin = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteBusAdmin.rejected,
        (state, action) => {
          state.deleteBusAdmin = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteBusAdmin() {
    return {
      ...builder.addCase(extraActions.resetDeleteBusAdmin, (state, action) => {
        state.resetDeleteBusAdmin = ResetApiState(true);
        state.deleteBusAdmin = ResetApiState(null);
      }),
    };
  }

  function busAdminsReducer() {
    return {
      ...builder.addCase(extraActions.getBusAdmins.pending, (state, action) => {
        state.busAdmins = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getBusAdmins.fulfilled,
        (state, action) => {
          state.busAdmins = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getBusAdmins.rejected,
        (state, action) => {
          state.busAdmins = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedBusAdminsReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedBusAdmins.pending,
        (state, action) => {
          state.pagedBusAdmins = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedBusAdmins.fulfilled,
        (state, action) => {
          state.pagedBusAdmins = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedBusAdmins.rejected,
        (state, action) => {
          state.pagedBusAdmins = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function busAdminReducer() {
    return {
      ...builder.addCase(extraActions.getBusAdmin.pending, (state, action) => {
        state.busAdmin = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getBusAdmin.fulfilled,
        (state, action) => {
          state.busAdmin = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.getBusAdmin.rejected, (state, action) => {
        state.busAdmin = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetBusAdmin() {
    return {
      ...builder.addCase(extraActions.resetBusAdmin, (state, action) => {
        state.resetBusAdmin = ResetApiState(true);
        state.busAdmin = ResetApiState(null);
      }),
    };
  }

  function resetPasswordReducer() {
    return {
      ...builder.addCase(
        extraActions.resetPassword.pending,
        (state, action) => {
          state.resetPassword = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.resetPassword.fulfilled,
        (state, action) => {
          state.resetPassword = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.resetPassword.rejected,
        (state, action) => {
          state.resetPassword = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetResetPassword() {
    return {
      ...builder.addCase(extraActions.resetResetPassword, (state, action) => {
        state.resetResetPassword = ResetApiState(true);
        state.resetPassword = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createBusAdmin = ResetApiState(null);
        state.resetCreateBusAdmin = ResetApiState(null);
        state.editBusAdmin = ResetApiState(null);
        state.resetEditBusAdmin = ResetApiState(null);
        state.deleteBusAdmin = ResetApiState(null);
        state.resetDeleteBusAdmin = ResetApiState(null);
        state.busAdmins = ResetApiState([]);
        state.busAdmin = ResetApiState(null);
        state.resetBusAdmin = ResetApiState(null);
        state.resetBusAdmin = ResetApiState(null);
        state.rolePermissions = ResetApiState(null);
      }),
    };
  }
}

const BusAdminSlice = createSlice({
  name,
  initialState: BusAdminInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const BusAdminActions = { ...BusAdminSlice.actions, ...extraActions };
export const BusAdminReducer = BusAdminSlice.reducer;
